import { api } from '../../../lib/api';
import type { PagedResult } from '../../administration/users/userTypes';
import type {
  EvidenceEligibility,
  EvidenceRequestCaseOption,
  EvidenceRequestDetail,
  EvidenceRequestListItem,
  EvidenceRequestResult,
  EvidenceRequestableItem,
  RequestEvidenceReceivingRequest,
  RequestEvidenceTransferRequest,
} from '../../evidence-management/evidence/evidenceTypes';

/** What the register may be narrowed by. */
export interface EvidenceRequestListQuery {
  search?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  /** The ROW's own live-or-deleted state — the shell's key. */
  status?: string;
  /** The lifecycle. Named `stage` because `status` is the shell's. */
  stage?: string;
  kind?: string;
}

function toQueryString(query: EvidenceRequestListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);

  // Written whenever the key is PRESENT, empty string included — the three-state facet rule.
  if (query.status !== undefined) params.set('status', query.status);
  if (query.stage !== undefined) params.set('stage', query.stage);
  if (query.kind !== undefined) params.set('kind', query.kind);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

/** A null field is LEFT OUT rather than sent as "null", which the binder would read as a string. */
function append(body: FormData, key: string, value: unknown) {
  if (value !== null && value !== undefined) body.append(key, String(value));
}

const BASE = '/api/self-service/evidence-requests';

/**
 * The requester's own evidence requests.
 *
 * **THERE IS NO SCOPE PARAMETER, and that is not an omission.** Which requests come back is fixed by
 * the ROUTE server-side, so nothing this client sends can widen it — the requisition slip's rule.
 *
 * **Nothing here moves evidence.** The case's team fulfils a request through the case's own Receive
 * and Transfer, under their own keys, flags, password and approval.
 */
export const evidenceRequestsApi = {
  list: (query: EvidenceRequestListQuery, signal?: AbortSignal) =>
    api<PagedResult<EvidenceRequestListItem>>(`${BASE}${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) => api<EvidenceRequestDetail>(`${BASE}/${id}`, { signal }),

  /** Whether the caller is an employee — announced by the index page before the refusal fires. */
  eligibility: (signal?: AbortSignal) => api<EvidenceEligibility>(`${BASE}/eligibility`, { signal }),

  /** The cases the caller may raise a request on. Carries `create`. */
  cases: (signal?: AbortSignal) => api<EvidenceRequestCaseOption[]>(`${BASE}/cases`, { signal }),

  /** A case's stored items a transfer request may name. Carries `create`. */
  items: (caseId: string, signal?: AbortSignal) =>
    api<EvidenceRequestableItem[]>(`${BASE}/cases/${caseId}/items`, { signal }),

  /** Each row's fields as `rows[i].field` and its documents as `rows[i].attachments` — the names the controller reads. */
  raiseReceiving: (request: RequestEvidenceReceivingRequest, attachments: File[][]) => {
    const body = new FormData();
    append(body, 'evidenceCaseId', request.evidenceCaseId);

    request.rows.forEach((row, index) => {
      for (const [key, value] of Object.entries(row)) append(body, `rows[${index}].${key}`, value);
      for (const file of attachments[index] ?? []) body.append(`rows[${index}].attachments`, file);
    });

    return api<EvidenceRequestResult>(`${BASE}/receiving`, { method: 'POST', body });
  },

  raiseTransfer: (request: RequestEvidenceTransferRequest, courtOrder: File | null) => {
    const { itemIds, ...fields } = request;
    const body = new FormData();

    for (const [key, value] of Object.entries(fields)) append(body, key, value);
    for (const id of itemIds) body.append('itemIds', id);
    if (courtOrder) body.append('courtOrder', courtOrder);

    return api<EvidenceRequestResult>(`${BASE}/transfer`, { method: 'POST', body });
  },

  cancel: (id: string) => api<EvidenceRequestDetail>(`${BASE}/${id}/cancel`, { method: 'POST' }),
};
